import _ from 'lodash';
import * as pipeDefinitions from './pipeDefinitions';
import { indicatorCodeSnippets } from './snippets';
import { pipeInputSchemaTypes } from './pipeDefinitions/config';
import { SEGMENT_CONFIG } from 'common/constants';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const { isCrypto } = ApplicationDomainUtility;

export const PIPE_CATEGORIES = {
  snippet: 'snippet',
  customScript: 'customScript'
};

export const CANDLE_INTERVALS = [
  { displayValue: '1 minute', value: '1 minutes' },
  { displayValue: '3 minutes', value: '3 minutes' },
  { displayValue: '5 minutes', value: '5 minutes' },
  { displayValue: '10 minutes', value: '10 minutes' },
  { displayValue: '15 minutes', value: '15 minutes' },
  { displayValue: '30 minutes', value: '30 minutes' },
  { displayValue: '45 minutes', value: '45 minutes' },
  { displayValue: '1 hour', value: '1 hours' },
  { displayValue: '1 day', value: '1 days' },
];

const DISABLED_INDICATORS = (() => {
  const { isAdmin, SA_FEATURE_FLAGS = {} } = window;
  const list = [];
  if (!SA_FEATURE_FLAGS.isCustomScriptsEnabled) {
    list.push('customScript');
  }
  if (!isAdmin) {
    list.push('indiaVIXPercentile');
    list.push('customScript');
  }

  list.push(...(ApplicationDomainUtility.getUiConfig('disabled_indicators') || []));
  return list;
})();

export const removedIndicators = [
  'augmentedDickeyFuller',
  'indiaVIXPercentile',
  'zScore',
  'densityCurve',
  'ratioStandardDeviation',
  'renkoBearishBrick',
  'renkoBullishBrick',
];

const getSanitizedPipeDefinitions = () => {
  const isIndicatorCodeSnippetsEnabled = _.get(
    window, 'SA_FEATURE_FLAGS.isIndicatorCodeSnippetEnabled', false
  );

  if (!isIndicatorCodeSnippetsEnabled) return pipeDefinitions;
  const clonedDefinitions = _.cloneDeep({ ...pipeDefinitions });
  const snippets = indicatorCodeSnippets();

  snippets.forEach((snippet) => { clonedDefinitions[snippet.type] = snippet; });
  // return clonedDefinitions;
  return _.chain(clonedDefinitions)
    .mapValues((pipeDefinition) => {
      if (pipeDefinition?.configFormOptions?.isDayCandleEnabled) {
        return setDayCandleConfig(pipeDefinition);
      }
      return pipeDefinition;
    })
    .value();
};

export const getCandleInterval = (pipeType) => {
  const isDayCandleEnabled = PipeDefinitionUtility.getIsDayCandleEnabled(pipeType)
  return !isDayCandleEnabled
    ? _.filter(CANDLE_INTERVALS, (item) => item.value !== '1 days')
    : CANDLE_INTERVALS;
};

export const setDayCandleConfig = (pipeDefinition) => {
  if (!pipeDefinition?.configFormOptions?.isDayCandleEnabled) return pipeDefinition;
  return {
    ...pipeDefinition,
    inputSchema: [
      ...(pipeDefinition.inputSchema || []),
      {
        name: 'startWindow',
        type: pipeInputSchemaTypes.timeOfDay,
        label: 'Start Window',
      },
      {
        name: 'endWindow',
        type: pipeInputSchemaTypes.timeOfDay,
        label: 'End Window',
      }
    ],
  };
};

export const PipeDefinitionUtility = (() => {
  let definitions = getSanitizedPipeDefinitions();

  return {
    getPipeInputSchemaByType: (type) => {
      return _.get(definitions, [type, 'inputSchema'], []);
    },

    getDefinitions: () => definitions,
    setDefinitions: () => {
      definitions = getSanitizedPipeDefinitions();
    },
    getPipeConfigSearchTerms: () => {
      return _.chain(definitions)
        .reduce((acc, pipeDefinition, type) => {
          if (pipeDefinition?.isEnabled) {
            acc[type] = pipeDefinition;
          }
          return acc;
        }, {})
        .flatMap((pipeDefinition) => pipeDefinition.searchTerms.map((searchTerm) => ({
          label: searchTerm,
          value: pipeDefinition.type,
          priority: pipeDefinition?.priority
        })))
        .filter(({ value }) => !_.includes(DISABLED_INDICATORS, value))
        .sortBy('priority')
        .value();
    },
    getPipeConfigOptions: (pipeType) => _.get(
      definitions,
      [pipeType, 'configFormOptions', 'configFieldOptions'],
      {}
    ),
    getPipeConfigDefaults: (type) => {
      const defaultPipeConfig = _.get(definitions, [type, 'defaults'], {});
      const baseConfig = _.cloneDeep(defaultPipeConfig);

      if (isCrypto && baseConfig.config) {
        // TODO: Excluded the defaultInterval candleInterval for some indicator. Use alternative approach.
        const inputSchema = _.get(definitions, [type, 'inputSchema'], {});
        const shouldUpdateCandleInterval = baseConfig.config.candleInterval && _.get(_.keyBy(inputSchema, 'name'), 'candleInterval.isDisplay', true);

        baseConfig.config = {
          ...baseConfig.config,
          ...(shouldUpdateCandleInterval && { candleInterval: '15 minutes' }),
          ...(baseConfig.config.valuePaths && { valuePaths: ['item0.month1Price'] }),
        };
      }

      return baseConfig;
    },
    getPipeValuePathSupportedSegments: (type) => {
      return _.chain(definitions)
        .get([type, 'configFormOptions', 'valuePathSupportedSegments'], [])
        .filter((segment) => !isCrypto || segment !== SEGMENT_CONFIG.equity)
        .cloneDeep()
        .value();
    },
    getIsDayCandleEnabled: (type) => {
      return _.cloneDeep(
        _.get(definitions, [type, 'configFormOptions', 'isDayCandleEnabled'], false)
      );
    },
    getIndicatorsWithNonEditableValuePaths: () => _.chain(definitions)
      .filter((pipe) => !pipe?.configFormOptions?.valuePathEditable)
      .map((pipe) => pipe.type)
      .value()
  };
})();

// TODO configFieldOptions
export const configDisplayNames = {
  noOfCandles: 'Period',
  valuePaths: 'Field'
};
